// Night mode — the ADMIN, and for a STAFF member the rest of their app
// (owner specs 2026-10-02 and 2026-10-03).
//
// Three choices — Auto · Light · Dark — Auto by default, following the phone.
//
// WHOSE CHOICE (2026-10-03). The PERSON's, not the device's: saved on the
// server (profiles.theme_pref, migration 209, PUT /users/me/theme), so it
// follows them across navigation, refresh, reopening and devices, and another
// person signing in on the same phone keeps their own. localStorage keeps a
// copy per person — `storiz_theme:<user id>` — only so a page can be dark
// before React and the network exist (the boot script). It is written for
// STAFF only, so at boot it also says « this signed-in person is staff ».
// The device-wide key of 2026-10-02 (`storiz_admin_theme`) is read once, moved
// to the first staff member who signs in, and deleted.
//
// WHERE (inDarkScope). The admin; and for a staff member, the editor (with its
// photo and cover screens), the 3D preview and the entry page. NEVER for a
// customer: without a staff session every customer page is exactly as before.
// Never the headless render probes (/_…: print + preview rasterisation). The
// customer wizard (/flow), the upload and printer links and the login stay
// light on purpose — customer-facing screens staff only open to see what the
// customer sees. Photos, album pages, covers and 3D previews are never tinted
// anywhere: [data-adm-media] light islands (index.css).
//
// Three places act on the same decision, and must agree:
//   1. index.html — an inline <head> script, BEFORE the CSS paints, so a dark
//      person never flashes white on load. It mirrors bootTheme() below and is
//      pinned to it by bootScript.guard.test.ts (it runs the real script).
//   2. AdminAppShell — applyTheme() on every route change and on every change
//      of the preference, of the phone's setting or of who is signed in.
//   3. adminHead.ts — the theme-color tag (phone status bar) uses themeColorFor().
//
// The palette itself is CSS: see the « ADMIN NIGHT MODE » contract in index.css.
import { useSyncExternalStore } from 'react';
import { isAdminPath } from '@/lib/pwa/detect';

export type ThemePref = 'auto' | 'light' | 'dark';
export type ThemeMode = 'light' | 'dark';

/** The DEVICE-wide choice of 2026-10-02. Still the default on admin pages
 *  before anyone is known, and moved to the first staff member who signs in.
 *  Never written for an identified staff member (it would leak to the next). */
export const THEME_STORAGE_KEY = 'storiz_admin_theme';
/** The per-person boot cache: `storiz_theme:<user id>` → auto | light | dark. */
export const USER_THEME_PREFIX = 'storiz_theme:';
export const userThemeKey = (uid: string): string => USER_THEME_PREFIX + uid;
export const DARK_CLASS = 'adm-dark';
export const DARK_QUERY = '(prefers-color-scheme: dark)';

/** The page canvas behind everything (index.css --color-adm-canvas). The dark
 *  one sits between the 3D preview's #221A11 and #15100A. */
export const CANVAS = { light: '#FAF6EC', dark: '#1C150E' } as const;

/** Storiz gold — what the installed app's status bar has always used in light
 *  (lib/brand.ts BRAND.gold). Unchanged by night mode. */
export const LIGHT_THEME_COLOR = '#C9A24A';

const PREFS: readonly ThemePref[] = ['auto', 'light', 'dark'];

/** A real choice, or null (absent, junk). */
function asPref(raw: unknown): ThemePref | null {
  return typeof raw === 'string' && (PREFS as readonly string[]).includes(raw) ? (raw as ThemePref) : null;
}

export function parsePref(raw: unknown): ThemePref {
  return asPref(raw) ?? 'auto';
}

type StorageLike = Pick<Storage, 'getItem'> & Partial<Pick<Storage, 'setItem' | 'removeItem' | 'key' | 'length'>>;

function get(storage: StorageLike | null | undefined, key: string): string | null {
  try { return storage?.getItem(key) ?? null; } catch { return null; }
}
function put(storage: StorageLike | null | undefined, key: string, value: string): boolean {
  try { if (!storage?.setItem) return false; storage.setItem(key, value); return true; } catch { return false; }
}
function drop(storage: StorageLike | null | undefined, key: string): void {
  try { storage?.removeItem?.(key); } catch { /* blocked storage: nothing to drop */ }
}

export function readPref(storage: StorageLike | null | undefined, key: string = THEME_STORAGE_KEY): ThemePref {
  return parsePref(get(storage, key));
}

/** false when the browser refused to save (private window, blocked site data). */
export function writePref(storage: StorageLike | null | undefined, pref: ThemePref, key: string = THEME_STORAGE_KEY): boolean {
  return put(storage, key, pref);
}

/** supabase-js keeps the session at `sb-<project ref>-auth-token` (lib/supabase.ts
 *  AUTH_STORAGE_KEY). Any project: the local preview uses another one. */
const SESSION_KEY = /^sb-.+-auth-token$/;

/**
 * Who is signed in on this device, from the session supabase-js stored — the
 * only thing known before React and the network exist. null = nobody (or a
 * storage that cannot be read). Read-only: the session is never touched.
 */
export function sessionUserId(storage: StorageLike | null | undefined): string | null {
  if (!storage) return null;
  try {
    const n = storage.length ?? 0;
    for (let i = 0; i < n; i++) {
      const k = storage.key?.(i);
      if (!k || !SESSION_KEY.test(k)) continue;
      try {
        const s = JSON.parse(storage.getItem(k) ?? 'null');
        const id = s?.user?.id ?? s?.currentSession?.user?.id;
        if (typeof id === 'string' && id) return id;
      } catch { /* not a session */ }
    }
  } catch { /* blocked storage */ }
  return null;
}

/** A staff member's pages beyond the admin: the entry page (a spinner before
 *  the admin), the editor and its photo/cover screens, the 3D preview. */
const STAFF_PAGES = /^\/(?:$|editor\/|preview\/)/;

/**
 * Does night mode apply on this page? The admin: always (staff-only pages).
 * Elsewhere: only for a staff member, only on the pages above, never on the
 * headless render probes. A customer page without a staff session: never.
 */
export function inDarkScope(pathname: string, staff: boolean): boolean {
  if (pathname.startsWith('/_')) return false;
  if (isAdminPath(pathname)) return true;
  return staff && STAFF_PAGES.test(pathname);
}

export function resolveMode(pref: ThemePref, systemDark: boolean): ThemeMode {
  if (pref === 'auto') return systemDark ? 'dark' : 'light';
  return pref;
}

/** The three reads the boot script makes (it cannot import anything). */
export interface BootReads {
  /** The signed-in person (sessionUserId). */
  uid: string | null;
  /** Their cached choice — present only for staff. */
  userPref: string | null;
  /** The device-wide choice of 2026-10-02. */
  devicePref: string | null;
}

export function readBoot(storage: StorageLike | null | undefined): BootReads {
  const uid = sessionUserId(storage);
  return { uid, userPref: uid ? get(storage, userThemeKey(uid)) : null, devicePref: get(storage, THEME_STORAGE_KEY) };
}

/** What the boot script decides from its reads. null = not in scope: the
 *  script does nothing at all. */
export function bootDecision(pathname: string, reads: BootReads, systemDark: boolean): { dark: boolean } | null {
  const own = reads.uid ? asPref(reads.userPref) : null;
  if (!inDarkScope(pathname, own !== null)) return null;
  return { dark: resolveMode(own ?? parsePref(reads.devicePref), systemDark) === 'dark' };
}

/**
 * What the inline boot script in index.html decides, as a pure function.
 * null = not in scope: the script does nothing at all.
 */
export function bootTheme(pathname: string, storage: StorageLike | null | undefined, systemDark: boolean): { dark: boolean } | null {
  return bootDecision(pathname, readBoot(storage), systemDark);
}

/** The phone status bar (theme-color). Dark = the canvas, so the bar and the
 *  phone header under it are one surface. */
export function themeColorFor(mode: ThemeMode): string {
  return mode === 'dark' ? CANVAS.dark : LIGHT_THEME_COLOR;
}

/**
 * Put the document in the resolved mode. `inScope` false = take everything off
 * (the person left the pages night mode covers — inDarkScope), whatever the
 * preference says.
 */
export function applyTheme(doc: Document, inScope: boolean, mode: ThemeMode): void {
  const html = doc.documentElement;
  const dark = inScope && mode === 'dark';
  html.classList.toggle(DARK_CLASS, dark);
  // Native controls (date pickers, selects, scrollbars) follow the page.
  html.style.colorScheme = dark ? 'dark' : '';
  // The boot script painted the canvas inline before the CSS existed; from
  // here on index.css owns it (html.adm-dark { background-color }).
  html.style.backgroundColor = '';
  if (!html.getAttribute('style')) html.removeAttribute('style');

  let meta = doc.head.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta && !inScope) return; // a customer page that never saw the admin
  if (!meta) {
    meta = doc.createElement('meta');
    meta.name = 'theme-color';
    doc.head.appendChild(meta);
  }
  const color = themeColorFor(dark ? 'dark' : 'light');
  if (meta.content !== color) meta.content = color;
}

/** The mode the document is in right now (adminHead.ts reads it). */
export function currentMode(doc: Document): ThemeMode {
  return doc.documentElement.classList.contains(DARK_CLASS) ? 'dark' : 'light';
}

// ── The store: one preference, per person, live ───────────────────────────
interface MediaLike {
  readonly matches: boolean;
  addEventListener?: (type: 'change', fn: () => void) => void;
  removeEventListener?: (type: 'change', fn: () => void) => void;
}
export interface ThemeSnapshot {
  pref: ThemePref;
  mode: ThemeMode;
  /** The person on screen is staff (known from the profile, or from the boot
   *  cache until the profile arrives): night mode may follow them beyond the admin. */
  staff: boolean;
}

/** Who is signed in, as the profile says (AdminAppShell feeds it). */
export interface ThemeIdentity {
  uid: string;
  /** admin or staff. A customer never gets night mode outside the admin. */
  staff: boolean;
  /** profiles.theme_pref as the server sent it: a choice; null = never chosen;
   *  undefined = this server has no such column yet (migration 209 not applied). */
  serverPref?: string | null;
}

/** Saves the person's choice on the server; true once it is stored there. */
export type ThemeSaver = (pref: ThemePref) => Promise<boolean>;

export function createThemeStore(deps: {
  storage: () => StorageLike | null;
  media: () => MediaLike | null;
  save?: ThemeSaver;
}) {
  const listeners = new Set<() => void>();
  let saver: ThemeSaver | null = deps.save ?? null;
  // Before React knows the profile, the boot cache says who this is: the
  // signed-in person's own copy exists only if they are staff.
  const boot = readBoot(deps.storage());
  const own = boot.uid ? asPref(boot.userPref) : null;
  let uid: string | null = own ? boot.uid : null;
  let staff = own !== null;
  // In memory too: a storage that refuses to save must not stop the switch.
  let pref: ThemePref = own ?? parsePref(boot.devicePref);
  /** A change the server has not confirmed yet: it wins over the next profile
   *  load (which may still carry the old value) and is sent again then. */
  let pending: ThemePref | null = null;
  let mql: MediaLike | null = null;
  let snap: ThemeSnapshot | null = null;

  const systemDark = () => {
    try { return !!(mql ?? deps.media())?.matches; } catch { return false; }
  };
  const emit = () => { snap = null; listeners.forEach((f) => f()); };
  const onMedia = () => { if (pref === 'auto') emit(); else snap = null; };
  const persist = (p: ThemePref) => {
    pending = p;
    const save = saver;
    if (!save) return;
    void save(p).then((ok) => { if (ok && pending === p) pending = null; }, () => { /* kept pending */ });
  };
  const forget = () => {
    const changed = uid !== null || staff;
    uid = null;
    staff = false;
    pending = null;
    const device = readPref(deps.storage());
    if (changed || device !== pref) { pref = device; emit(); }
  };

  return {
    snapshot(): ThemeSnapshot {
      const mode = resolveMode(pref, systemDark());
      if (!snap || snap.pref !== pref || snap.mode !== mode || snap.staff !== staff) snap = { pref, mode, staff };
      return snap;
    },
    set(next: ThemePref) {
      if (next === pref) return;
      pref = next;
      const st = deps.storage();
      if (uid && staff) {
        put(st, userThemeKey(uid), next);
        persist(next);
      } else {
        writePref(st, next); // nobody known yet: the device default
      }
      emit();
    },
    /** The profile arrived (or the person signed out: null). */
    identify(id: ThemeIdentity | null) {
      const st = deps.storage();
      if (!id) { forget(); return; }
      if (!id.staff) {
        drop(st, userThemeKey(id.uid)); // never let the boot script treat them as staff
        forget();
        return;
      }
      const samePerson = uid === id.uid;
      if (!samePerson) pending = null;
      const server = asPref(id.serverPref);
      let next: ThemePref;
      let push: ThemePref | null = null;
      if (samePerson && pending) {
        next = pending;
        push = pending;
      } else if (server) {
        next = server;
      } else {
        // Nothing on the server yet: this person's copy here, else the old
        // device-wide choice (moved to them, once), else Auto.
        const cached = asPref(get(st, userThemeKey(id.uid)));
        const device = asPref(get(st, THEME_STORAGE_KEY));
        next = cached ?? device ?? 'auto';
        if (!cached && device) drop(st, THEME_STORAGE_KEY);
        // A server that has the column but no value gets the local choice;
        // one without the column (undefined) is not bothered.
        if (id.serverPref === null && (cached ?? device)) push = next;
      }
      put(st, userThemeKey(id.uid), next);
      const changed = !samePerson || !staff || pref !== next;
      uid = id.uid;
      staff = true;
      pref = next;
      if (push) persist(push);
      if (changed) emit();
    },
    /** How the choice reaches the server (wired by the app shell). */
    setSaver(fn: ThemeSaver | null) {
      saver = fn;
      if (fn && pending) persist(pending);
    },
    /** Another tab changed it (the `storage` event). */
    reload() {
      const st = deps.storage();
      const next = uid && staff ? (asPref(get(st, userThemeKey(uid))) ?? pref) : readPref(st);
      if (next !== pref) { pref = next; emit(); }
    },
    subscribe(fn: () => void): () => void {
      listeners.add(fn);
      if (listeners.size === 1) {
        try { mql = deps.media(); mql?.addEventListener?.('change', onMedia); } catch { mql = null; }
      }
      return () => {
        listeners.delete(fn);
        if (listeners.size === 0) {
          try { mql?.removeEventListener?.('change', onMedia); } catch { /* old browser */ }
          mql = null;
        }
      };
    },
  };
}

const browserStorage = (): Storage | null => { try { return window.localStorage; } catch { return null; } };
const browserMedia = (): MediaLike | null => {
  try { return typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(DARK_QUERY) : null; } catch { return null; }
};

/** The app-wide store (the More sheet, Settings, the header button and the
 *  editor all bind to it). */
export const adminTheme = createThemeStore({ storage: browserStorage, media: browserMedia });

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === THEME_STORAGE_KEY || (e.key ?? '').startsWith(USER_THEME_PREFIX)) adminTheme.reload();
  });
}

export function useAdminTheme(): ThemeSnapshot & { setPref: (p: ThemePref) => void } {
  const snap = useSyncExternalStore(adminTheme.subscribe, adminTheme.snapshot, adminTheme.snapshot);
  return { ...snap, setPref: adminTheme.set };
}
